import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { Recommendation } from '../components/types/analysis.types';

export function useRecommendationSelection(recommendations: Recommendation[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(recommendations.map((r) => r.id)),
  );
  const initializedRef = useRef(recommendations.length > 0);

  // When recommendations arrive asynchronously (initially empty, then populated),
  // select all by default — but only on the first non-empty arrival.
  useEffect(() => {
    if (recommendations.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
      setSelectedIds(new Set(recommendations.map((r) => r.id)));
    }
  }, [recommendations]);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === recommendations.length) {
        return new Set(); // Deselect all
      } else {
        return new Set(recommendations.map((r) => r.id)); // Select all
      }
    });
  }, [recommendations]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectedRecommendations = useMemo(() => {
    return recommendations.filter((r) => selectedIds.has(r.id));
  }, [recommendations, selectedIds]);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const allSelected = selectedIds.size === recommendations.length && recommendations.length > 0;
  const noneSelected = selectedIds.size === 0;

  return {
    selectedIds,
    selectedRecommendations,
    toggleSelection,
    toggleAll,
    clearSelection,
    isSelected,
    allSelected,
    noneSelected,
  };
}
